import nanoajax from 'nanoajax'
import Promise from 'es6-promise'

const nanoPromise = (params, type = 'json') => new Promise((resolve, reject) => {
  if (type === 'document') {
    params.responseType = 'document'
  }

  shopify.idToken()
    .then((token) => {
      params.headers = params.headers || {}
      params.headers.Authorization = `Bearer ${token}`

      nanoajax.ajax(
        params,
        (code, response) => {
          if (code === 200 && response) {
            if (type === 'json') {
              try {
                response = JSON.parse(response)
              } catch (e) {
              }
            }
            resolve(response)
          } else {
            reject(response)
          }
        }
      )
    })
    .catch((error) => {
      reject(error)
    })
})

/**
 * Sending GET request
 * @param endpoint
 * @param type
 */
const fetchData = (endpoint, type = 'json') => {
  const params = {
    url: endpoint,
    method: 'GET'
  }
  return nanoPromise(params, type)
}

/**
 * Sending POST request
 * @param endpoint
 * @param body
 * @param type
 */
const postData = (endpoint, body, type = 'json') => {
  const params = {
    url: endpoint,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(body)
  }
  return nanoPromise(params, type)
}

const toBase64 = file => new Promise((resolve, reject) => {
  const reader = new FileReader()
  reader.readAsDataURL(file)
  reader.onload = () => resolve(reader.result)
  reader.onerror = error => reject(error)
})

/**
 * Update object with new value
 * ref: https://redux.js.org/recipes/structuring-reducers/refactoring-reducer-example#extracting-utility-functions
 * @param oldObject
 * @param newValues
 * @returns {*}
 */
const updateObject = (oldObject, newValues) => {
  // Encapsulate the idea of passing a new object as the first parameter
  // to Object.assign to ensure we correctly copy data instead of mutating
  return Object.assign({}, oldObject, newValues)
}

/**
 * Update array value
 * ref: https://redux.js.org/recipes/structuring-reducers/refactoring-reducer-example#extracting-utility-functions
 * @param array
 * @param itemId
 * @param updateItemCallback
 * @returns {*}
 */
const updateItemInArray = (array, itemId, updateItemCallback) => {
  return array.map(item => {
    if (item.id !== itemId) {
      // Since we only want to update one item, preserve all others as they are now
      return item
    }

    // Use the provided callback to create an updated item
    return updateItemCallback(item)
  })
}

/**
 * id dev env
 * @type {boolean}
 */
const isDev = process.env.NODE_ENV === 'development'

/**
 * Get app's related data
 * @returns {DOMStringMap}
 */
const getAppData = () => {
  return document.getElementById('shopify-app-init').dataset
}

/**
 * Extract id from graphql ID: gid://shopify/ProductVariant/1234567 => 1234567
 * @param string
 * @returns {number|*}
 */
const extractIdFromGraphQL = (string) => {
  if (string && string.indexOf('/') === -1) {
    return string
  }
  return parseInt(string.split('/').pop())
}

/**
 * Remove whitespace, new line from HTML string
 * @param html
 * @returns {*}
 */
const formatHTML = (html) => {
  return html.replace(/\n/g, "") // remove newline / carriage return
    .replace(/[\t ]+\</g, "<") // remove whitespace (space and tabs) before tags
    .replace(/\>[\t ]+\</g, "><") // remove whitespace between tags
    .replace(/\>[\t ]+$/g, ">") // remove whitespace after tags
    .replace(/ +(?= )/g, '') // remove multiple whitespace
}

/**
 * Sort array of objects based on another array
 * @param array
 * @param order
 * @param key
 * @returns {*}
 */
const mapOrder = (array, order, key) => {
  return [...array].sort( function (a, b) {
    const A = a[key], B = b[key]
    if (order.indexOf(A) > order.indexOf(B)) {
      return 1
    } else {
      return -1
    }
  })
}

/**
 * Move an array element from one position to another
 * @param arr
 * @param oldIndex
 * @param newIndex
 * @returns {*}
 */
const arrayMove = (arr, oldIndex, newIndex) => {
  while (oldIndex < 0) {
    oldIndex += arr.length
  }
  while (newIndex < 0) {
    newIndex += arr.length
  }
  if (newIndex >= arr.length) {
    let k = newIndex - arr.length
    while ((k--) + 1) {
      arr.push(undefined)
    }
  }
  arr.splice(newIndex, 0, arr.splice(oldIndex, 1)[0])
  return arr
}

/**
 * Check if variable is set or not
 * @param ref
 * @returns {boolean}
 */
const isset = (ref) => typeof ref !== 'undefined'

/**
 * Rename an object key and return new object
 * @param object
 * @param key
 * @param newKey
 * @returns {*}
 */
const renameKey = (object, key, newKey) => {
  const clonedObj = {...object}
  const targetKey = clonedObj[key]
  delete clonedObj[key]
  clonedObj[newKey] = targetKey
  return clonedObj
}

/**
 * Check is number (both positive and negative number)
 * @param value
 * @returns {boolean}
 */
const isNumeric = (value) => {
  return /^-{0,1}\d+$/.test(value)
}

export {
  fetchData,
  postData,
  toBase64,
  updateObject,
  updateItemInArray,
  isDev,
  getAppData,
  extractIdFromGraphQL,
  formatHTML,
  mapOrder,
  arrayMove,
  isset,
  renameKey,
  isNumeric
}
